import {
    CardHeader,
    Divider,
    Card as FluentUICard,
    InfoLabel,
    type Slot,
    Spinner,
    mergeClasses,
} from '@fluentui/react-components';
import { type CSSProperties, type ReactNode, useCallback } from 'react';
import { useSharedStyles } from '../../styles.js';
import { useStyles } from './styles.js';

type CardProps = {
    className?: string;
    style?: CSSProperties;
    columns?: 2 | 3;
    rows?: 2 | 3;
    title: string;
    description: string;
    tooltip?: string;
    action?: ReactNode;
    headerIcon?: Slot<'div', 'img'>;
    isFetching?: boolean;
    children: ReactNode;
    hideOverflow?: boolean;
};

export function Card({
    className,
    style,
    columns,
    rows,
    title,
    description,
    tooltip,
    action,
    headerIcon,
    isFetching,
    children,
    hideOverflow,
}: CardProps) {
    const styles = useStyles();
    const sharedStyles = useSharedStyles();

    const Header = useCallback(() => {
        return tooltip ? <InfoLabel info={tooltip}>{title}</InfoLabel> : title;
    }, [title, tooltip]);

    const Description = useCallback(() => {
        return <div className={styles.description}>{description}</div>;
    }, [description, styles]);

    return (
        <FluentUICard
            className={mergeClasses(
                // rows
                !rows && sharedStyles.oneRow,
                rows === 2 && sharedStyles.twoRows,
                rows === 3 && sharedStyles.threeRows,
                // columns
                !columns && sharedStyles.oneColumn,
                columns === 2 && sharedStyles.twoColumns,
                columns === 3 && sharedStyles.threeColumns,
                // styles
                styles.card,
                styles.removePadding,
                hideOverflow && styles.hideOverflow,
                className
            )}
            style={style}
        >
            <div className={styles.container}>
                <CardHeader
                    className={styles.header}
                    header={<Header />}
                    description={<Description />}
                    image={headerIcon}
                    action={
                        <div className={styles.action}>
                            {isFetching && <Spinner size="tiny" />}

                            {action}
                        </div>
                    }
                />

                <Divider />

                {children}
            </div>
        </FluentUICard>
    );
}
